/* eslint-disable @typescript-eslint/no-explicit-any -- Service layer uses dynamic types for external API responses */
import type { WhiskState } from '@/core/store/slices/creative';
export type { WhiskState };

import type { AgentMessage } from '@/core/store/slices/agent';
export type { AgentMessage };
import { UserProfile, BrandKit } from '@/modules/workflow/types';
import { INDII_MESSAGES } from './constants';
import type { AgentIdentityCard } from './governance/AgentIdentity';
import { AgentCard } from './a2a/AgentCard';
export type { AgentCard };
import type { Directive } from '@/services/directive/DirectiveTypes';
export type { Directive };

export type SchemaType = 'STRING' | 'NUMBER' | 'INTEGER' | 'BOOLEAN' | 'ARRAY' | 'OBJECT' | 'string' | 'number' | 'integer' | 'boolean' | 'array' | 'object';

export interface ToolParameterSchema {
    type: SchemaType;
    description?: string;
    enum?: string[];
    default?: string | number | boolean;
    items?: ToolParameterSchema;
    properties?: Record<string, ToolParameterSchema>;
    required?: string[];
}

export interface ToolParameters {
    type: 'OBJECT' | 'object';
    properties: Record<string, ToolParameterSchema>;
    required?: string[];
}

import { ZodType } from 'zod';

export type ToolRiskTier = 'read' | 'write' | 'destructive';
export type PermissionTier = 'builtin' | 'core' | 'plugin';

export interface ToolRiskMetadata {
    riskTier: ToolRiskTier;
    permissionTier: PermissionTier;
    requiresApproval: boolean;
    description: string;
}

export interface FunctionDeclaration {
    name: string;
    description: string;
    parameters: ToolParameters;
    schema?: ZodType;
    /** Reviewed metadata for a trusted agent-local tool absent from the central registry. */
    riskMetadata?: ToolRiskMetadata;
    /**
     * Risk classification for governance and permission gating.
     * - 'read': Safe, no side effects (e.g., list_projects, recall_memories)
     * - 'write': Creates or mutates data (e.g., create_project, save_memory)
     * - 'destructive': Irreversible or high-impact (e.g., rotate_credentials, deploy)
     * Legacy display hint only; dispatch requires riskMetadata or a registry entry.
     */
    riskTier?: ToolRiskTier;
}

export interface ToolDefinition {
    functionDeclarations: FunctionDeclaration[];
}

// ============================================================================
// Agent Identification
// ============================================================================


/**
 * All valid agent IDs that can be used with delegate_task.
 * This is the single source of truth for agent ID validation.
 *
 * IMPORTANT: Keep this in sync when adding new agents.
 * Used to prevent Autonomous hallucination of non-existent agent IDs.
 */
export const VALID_AGENT_IDS = [
    'marketing',
    'legal',
    'legal.contracts',
    'legal.compliance',
    'finance',
    'finance.accounting',
    'finance.tax',
    'finance.royalty',
    'producer',
    'director',
    'screenwriter',
    'video',
    'social',
    'publicist',
    'road',
    'hospitality',   // Venue hospitality & artist care for tours/events
    'event-planner', // End-to-end event production & coordination
    'creative',
    'publishing',
    'licensing',
    'rights',       // Rights & Registration orchestration (filing packets, portal workflows)
    'brand',
    'devops',
    'security',
    'merchandise',  // Merchandise creation & production
    'distribution', // Industrial Direct-to-DSP Engine
    'music',        // Sonic Director - Audio Analysis & Metadata
    'analytics',    // Intelligence Analytics Specialist
    'curriculum',   // Music Education Specialist
    'keeper',       // Context Integrity Guardian
    'generalist'  // indii Conductor (Hub)
] as const;

export type ValidAgentId = typeof VALID_AGENT_IDS[number];

/**
 * Comma-separated list of valid agent IDs for use in tool descriptions.
 * Prevents Autonomous from hallucinating non-existent agent names.
 */
export const VALID_AGENT_IDS_LIST = VALID_AGENT_IDS.join(', ');

export type AgentCategory = 'manager' | 'department' | 'specialist';

// ============================================================================
// Hub-and-Spoke Architecture (Phase 4)
// ============================================================================

/**
 * The hub agent in the hub-and-spoke architecture.
 * All specialist agents must delegate through the hub.
 */
export const HUB_AGENT_ID = 'generalist';

/**
 * Specialist agents (spokes) that can only delegate to the hub.
 * These agents represent domain expertise and should not delegate to each other directly.
 */
export const SPOKE_AGENT_IDS = VALID_AGENT_IDS.filter(id => id !== HUB_AGENT_ID);

/**
 * Checks if an agent is the hub (generalist / indii Conductor).
 */
export function isHubAgent(agentId: string): boolean {
    return agentId === HUB_AGENT_ID;
}

/**
 * Checks if an agent is a spoke (specialist).
 */
export function isSpokeAgent(agentId: string): boolean {
    return (SPOKE_AGENT_IDS as readonly string[]).includes(agentId);
}

/**
 * Validates hub-and-spoke architecture rules.
 * Returns null if valid, or an error message if invalid.
 *
 * Rules:
 * - Hub can delegate to any spoke
 * - Spokes can only delegate to hub
 * - Spokes CANNOT delegate to other spokes
 */
export function validateHubAndSpoke(sourceAgentId: string, targetAgentId: string): string | null {
    // Hub can delegate to anyone
    if (isHubAgent(sourceAgentId)) {
        return null;
    }

    // Hierarchical allowance: Head -> Worker (e.g., finance -> finance.tax)
    if (targetAgentId && sourceAgentId && targetAgentId.startsWith(`${sourceAgentId}.`)) {
        return null;
    }

    // Hierarchical allowance: Worker -> Head (e.g., finance.tax -> finance)
    if (sourceAgentId && targetAgentId && sourceAgentId.startsWith(`${targetAgentId}.`)) {
        return null;
    }

    // Spokes can only delegate to hub
    if (isSpokeAgent(sourceAgentId)) {
        if (isHubAgent(targetAgentId)) {
            return null; // Spoke -> Hub is allowed
        }
        return INDII_MESSAGES.hubSpokeViolation(sourceAgentId, targetAgentId);
    }

    // Unknown agent (shouldn't happen due to earlier validation)
    return `Unknown source agent: ${sourceAgentId}`;
}

// ============================================================================
// Agent Context Types
// ============================================================================

export interface ProjectHandle {
    id: string;
    name: string;
    type: string;
}

export interface DistributorInfo {
    name: string | null;
    isConfigured: boolean;
    coverArtSize: { width: number; height: number };
    audioFormat: string[];
    promptContext: string;
}

/**
 * Privacy-sanitized user intent captured before Boardroom-only prompt
 * augmentation. This is the sole input for deterministic intent matching.
 */
export interface BoardroomDispatchTask {
    readonly rawUserUtterance: string;
}

export interface AgentContext {
    agentId?: string;
    userId?: string;
    orgId?: string;
    projectId?: string;
    projectHandle?: ProjectHandle;
    chatHistory?: AgentMessage[];
    chatHistoryString?: string;
    brandKit?: BrandKit;
    memoryContext?: string;
    relevantMemories?: string[];
    userAlignmentRules?: string[]; // Injected strategic alignment rules
    ambitionLevel?: 'focused' | 'balanced' | 'ideas'; // Judgment layer: user-owned dial for offered-idea volume
    ragCorpus?: string;
    activeModule?: string;
    conversationMode?: 'direct' | 'department' | 'boardroom';
    /** Runtime-enforced observation mode; models cannot opt themselves out. */
    isReadOnly?: boolean;
    /** Application-owned assignment of the user's Boardroom request. */
    boardroomExecution?: { requestId: string; stepId: string; ownerAgentId: string; sourceId?: string };
    /** Frozen source references for tools; inline pixels never enter model prompts. */
    assetReferences?: ReadonlyArray<{ id: string; alias: string; url: string }>;
    /** Frozen source labels/IDs for planning, without private media URLs or bytes. */
    boardroomSourceManifest?: string;
    /** Cancellation propagated to long-running tools as well as model calls. */
    signal?: AbortSignal;
    seatedAgents?: string[];
    readonly boardroomTask?: Readonly<BoardroomDispatchTask>;
    userProfile?: UserProfile;
    distributor?: DistributorInfo;
    traceId?: string;
    swarmId?: string | null;
    attachments?: { mimeType: string; base64: string }[];
    systemPrompt?: string;
    whiskState?: WhiskState;
    livingContext?: string;
    getMetadata?: (key: string) => any;
    setMetadata?: (key: string, value: any) => void;
    /** Shared memory context explicitly passed between agents during delegation */
    sharedContext?: string;
    /** Durable informational notes routed to this agent; never executable instructions. */
    interAgentNotes?: Array<{ fromAgentId: string; content: string; createdAt?: number }>;
    /** When set by ProactiveService, carries the triggering proactive task metadata */
    proactiveTask?: ProactiveTask;
    /** The trigger type that caused this agent execution (schedule, event, etc.) */
    triggerType?: ProactiveTriggerType;
    /** Breaking circular dependency: Runner provided at runtime */
    runAgent?: AgentRunner;
    /**
     * Cryptographic identity card for the executing agent.
     * Injected by BaseAgent during execution for provenance tracking.
     * @see AgentIdentityCard
     */
    agentIdentity?: AgentIdentityCard;

    // ---- Phase 2: Agent Orchestration & Memory ----

    /**
     * When true, BaseAgent runs the ReflectionLoop after generating output.
     * The loop re-evaluates quality and may trigger additional iterations.
     * @default false
     */
    enableReflection?: boolean;

    /**
     * Prior turn context frames from ContextStackService.
     * Injected before agent execution to provide multi-turn reasoning state.
     */
    contextStack?: ContextFrame[];

    /** Compressed summary of the full context stack */
    contextSummary?: string;
    /** Active session ID */
    sessionId?: string;
    /** Current directive guiding agent behavior */
    directive?: Directive;
    /** Streaming runner for progressive specialist delegation */
    streamAgent?: (agentId: string, task: string, onChunk: (chunk: string) => void) => Promise<void>;
    /** Emit a single token to the user-facing UI during streaming */
    emitToken?: (token: string) => void;
    /** Injected hermetic product skill playbook XML block */
    activeProductSkillBlock?: string;
    /** Injected Tier 0 Artist Master Directive (living supreme playbook) */
    artistMasterDirectiveBlock?: string;
}

export type AgentRunner = (
    agentId: string,
    task: string,
    context?: AgentContext,
    traceId?: string,
    attachments?: { mimeType: string; base64: string }[]
) => Promise<AgentResponse>;

export type ProactiveTriggerType = 'schedule' | 'event' | 'proactive_trigger';

export interface ProactiveTask {
    id: string;
    agentId: string;
    task: string;
    triggerType: ProactiveTriggerType;
    executeAt?: number; // timestamp
    eventPattern?: string; // e.g. 'TASK_COMPLETED' or regex
    status: 'pending' | 'executing' | 'completed' | 'failed';
    createdAt: number;
    lastError?: string;
    userId: string;
}

// Using types from @/modules/workflow/types via imports above

// ============================================================================
// Tool Function Types
// ============================================================================

export type ToolFunctionArgs = Record<string, unknown>;

export interface AgentExecutionFailure {
    code: 'TOOL_NOT_AUTHORIZED' | 'AGENT_EXECUTION_FAILED';
    retryable: false;
    agentId: string;
    toolName?: string;
    recovery: 'choose_authorized_agent' | 'review_failure';
}

export interface ToolFunctionResult {
    failure?: AgentExecutionFailure;
    success: boolean;
    data?: any;
    error?: string;
    message?: string;
    /** Allows tools to set explicit execution states (e.g., pausing for approval) */
    status?: WorkflowExecutionStatus;
    /** URLs of generated or modified assets (images, audio, etc.) */
    urls?: string[];
    /** URL of a generated document (PDF, etc.) */
    documentUrl?: string;
    /** Annotations or structured metadata about the result */
    annotations?: any[];
    /** Metadata for tracing and debugging (e.g. latency, model version used) */
    metadata?: Record<string, unknown>;
}

import type { ToolExecutionContext } from './ToolExecutionContext';

export interface DelegateTaskArgs extends ToolFunctionArgs {
    targetAgentId: ValidAgentId;
    task: string;
}

export interface ExpertConsultation {
    targetAgentId: ValidAgentId;
    task: string;
}

export interface ConsultExpertsArgs extends ToolFunctionArgs {
    consultations: ExpertConsultation[];
}

export interface ShareNoteArgs extends ToolFunctionArgs {
    targetAgentId: ValidAgentId;
    content: string;
}

/**
 * Tool function type - accepts any args that extend ToolFunctionArgs
 * The runtime will validate args against the tool schema.
 * All tools MUST return a ToolFunctionResult for standardization.
 *
 * Phase 3: Added ToolExecutionContext for isolated state management.
 */
export type ToolFunction<TArgs extends ToolFunctionArgs = ToolFunctionArgs> = (
    args: TArgs,
    context?: AgentContext,
    toolContext?: ToolExecutionContext
) => Promise<ToolFunctionResult>;

/**
 * Generic tool function type for agent configs
 * Uses contravariance to accept more specific arg types
 */

export type AnyToolFunction = (...args: any[]) => Promise<ToolFunctionResult>;

// ============================================================================
// Agent Configuration Types
// ============================================================================

export interface AgentConfig {
    // ValidAgentId provides strict typing while allowing legacy agents via the union
    id: ValidAgentId;
    name: string;
    description: string;
    color: string;
    category: AgentCategory;
    systemPrompt: string;
    tools: ToolDefinition[];
    functions?: Record<string, AnyToolFunction>;
    /** Explicit allowlist of tool names this agent may invoke at runtime.
     *  If omitted, defaults to the names declared in tools[0].functionDeclarations.
     *  An empty array [] means the agent has NO tool access at runtime.
     */
    authorizedTools?: string[];
    /** Optional explicit fine-tuned model endpoint. When omitted, BaseAgent
     *  resolves a required Vertex endpoint from fine-tuned-models.ts.
     *  Agent execution must not silently downgrade to a base Gemini model.
     *  Format:
     *  "tunedModels/{tunedModelName}" or full Vertex endpoint URI.
     */
    modelId?: string;
    /**
     * Pre-minted cryptographic identity card. If not provided,
     * BaseAgent will mint one automatically during construction.
     */
    identityCard?: AgentIdentityCard;
    /** Hard cap on model output tokens per LLM call in the agent loop.
     *  Defaults to INTELLIGENCE_CONFIG.TEXT.MAX_OUTPUT_TOKENS_AGENT. */
    maxOutputTokens?: number;
    /** Max agentic-loop iterations (tool-call rounds) per task. Defaults to 8. */
    maxIterations?: number;
}

export interface AgentResponse {
    failure?: AgentExecutionFailure;
    text: string;
    data?: unknown;
    toolCalls?: Array<{
        name: string;
        args: ToolFunctionArgs;
        result: ToolFunctionResult | string;
    }>;
    thoughts?: string[];
    thoughtSignature?: string;
    error?: string;
    usage?: {
        promptTokens: number;
        completionTokens: number;
        totalTokens: number;
    };
}

export type AgentProgressCallback = (event: {
    type: 'thought' | 'tool' | 'token' | 'usage' | 'tool_result' | 'instrument execution';
    content: string;
    toolName?: string;
    usage?: {
        promptTokens: number;
        completionTokens: number;
        totalTokens: number;
        estimatedCost?: number;
    }
}) => void;

export interface SpecializedAgent {
    id: string;
    name: string;
    description: string;
    color: string;
    category: AgentCategory;
    /** Swarm identity card for A2A communication */
    card?: AgentCard;
    execute(task: string, context?: AgentContext, onProgress?: AgentProgressCallback, signal?: AbortSignal, attachments?: { mimeType: string; base64: string }[]): Promise<AgentResponse>;
}

export interface AgentRegistryProvider {
    getAsync(id: string, retryCount?: number): Promise<SpecializedAgent | undefined>;
    getLoadError(id: string): { error: Error; timestamp: number; attempts: number } | undefined;
    getAll(): SpecializedAgent[];
}

// ============================================================================
// Workflow Execution State Machine (Priority 1: Agentic Harness Primitive #4)
// ============================================================================

import type {
    WorkflowExecutionStatus,
    WorkflowStepStatus,
    WorkflowEdge,
    WorkflowStep,
    WorkflowStepExecution,
    WorkflowExecution
} from '@indii/shared';

export type {
    WorkflowExecutionStatus,
    WorkflowStepStatus,
    WorkflowEdge,
    WorkflowStep,
    WorkflowStepExecution,
    WorkflowExecution
};


/**
 * A single frame in the multi-turn context stack.
 * Represents one conversational turn's state snapshot.
 */
export interface ContextFrame {
    /** Unique identifier for this turn */
    turnId: string;
    /** When this turn occurred */
    timestamp: number;
    /** The user's message for this turn */
    userMessage: string;
    /** The agent's response for this turn */
    agentResponse: string;
    /** Tool calls made during this turn */
    toolCalls: { name: string; args: unknown; result: unknown }[];
    /** Key decisions made this turn (for summarization) */
    decisions: string[];
    /** Memory keys written during this turn (for rollback tracking) */
    memoryWrites: string[];
}

/**
 * Result of a ReflectionLoop evaluation pass.
 * Determines whether the agent should re-iterate on its output.
 */
export interface ReflectionResult {
    /** Whether the agent should produce another iteration */
    shouldIterate: boolean;
    /** Quality score from 0-10 (threshold: 7) */
    score: number;
    /** Specific feedback for improvement */
    feedback: string;
    /** Which iteration this result is from (1-indexed) */
    iterationCount: number;
    /** Hard cap on iterations (default: 3) */
    maxIterations: number;
}

/**
 * Callbacks for token-by-token streaming from AgentStreamingService.
 */
export interface StreamCallbacks {
    /** Called for each text token received */
    onToken: (token: string) => void;
    /** Called when the agent invokes a tool mid-stream */
    onToolCall: (name: string, args: Record<string, unknown>) => void;
    /** Called when a tool execution completes */
    onToolResult: (name: string, result: unknown) => void;
    /** Called when streaming is fully complete */
    onComplete: (response: AgentResponse) => void;
    /** Called on any error during streaming */
    onError: (error: Error) => void;
}
// ============================================================================
// Phase 4: Graph-Based Orchestration (NEXT)
// ============================================================================

/**
 * A specialized node in an Agentic Graph.
 * Nodes represent individual agent executions or logic gates.
 */
export interface GraphNode {
    id: string;
    agentId: ValidAgentId;
    /** The task template for this node. Can use placeholders like {{input}}. */
    taskTemplate: string;
    /** Whether this node must wait for all parents or just any parent. */
    waitCondition: 'all' | 'any';
    /** Optional hardcoded context overrides for this node. */
    contextOverrides?: Partial<AgentContext>;
    /** Optional flag indicating human in the loop approval is required before execution. */
    requiresApproval?: boolean;
}

/**
 * A directed edge between graph nodes.
 * Edges represent data flow and execution order.
 */
export interface GraphEdge {
    sourceId: string;
    targetId: string;
    /** Optional condition to traverse this edge (e.g. tool output matches regex). */
    condition?: string;
    /** Maps source output to target input placeholders. */
    inputMapping?: Record<string, string>;
}

/**
 * A non-linear workflow represented as a Directed Acyclic Graph (DAG).
 * Maps to GEAP Pillar 3: Graph-Based Orchestration.
 */
export interface AgentGraph {
    id: string;
    name: string;
    description: string;
    nodes: GraphNode[];
    edges: GraphEdge[];
    /** The node where execution begins. */
    entryNodeId: string;
    /** Metadata for tracking versioning and ownership. */
    metadata: {
        version: string;
        author: string;
        createdAt: number;
    };
}

/**
 * The execution state of a running graph.
 */
export interface GraphExecutionState {
    graphId: string;
    executionId: string;
    /** Map of node ID to its current execution status and output. */
    nodeStates: Record<string, {
        status: WorkflowStepStatus;
        output?: string;
        error?: string;
        startedAt?: number;
        completedAt?: number;
    }>;
    /** Overall status of the graph execution. */
    status: WorkflowExecutionStatus;
    /** Snapshot of the graph definition at the time of execution start. */
    graph?: AgentGraph;
    /** Arbitrary execution metadata (e.g. initial input). */
    metadata?: Record<string, any>;
    /** Distributed concurrency lease for multi-runner coordination. */
    lease?: {
        holderId: string;
        expiresAt: number;
        acquiredAt: number;
    } | null;
}
